import { format, formatDistanceToNow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { toRiyadhWallClockDate } from './formatDate';

const locales = { ar, en: enUS };

// All display formatters below are pinned to Asia/Riyadh: we route the input
// through a bridge Date whose *local* components match Riyadh wall clock,
// then date-fns renders those local components. This keeps timestamps
// consistent for KSA merchants regardless of the browser timezone.

export function formatDate(date: string | Date, pattern = 'dd MMM yyyy', lang = 'ar'): string {
  return format(toRiyadhWallClockDate(date), pattern, {
    locale: locales[lang as keyof typeof locales] || ar,
  });
}

export function formatDateTime(date: string | Date, lang = 'ar'): string {
  return formatDate(date, 'dd MMM yyyy, HH:mm', lang);
}

export function formatRelativeTime(date: string | Date, lang = 'ar'): string {
  // Relative phrasing ("2 hours ago") is a pure ms-diff and is already
  // timezone-independent, so no wall-clock shift is needed here.
  const d = typeof date === 'string' ? new Date(date) : date;
  return formatDistanceToNow(d, {
    addSuffix: true,
    locale: locales[lang as keyof typeof locales] || ar,
  });
}

export function formatCurrency(amount: number, currency = 'SAR', _lang = 'ar'): string {
  const hasDecimals = amount % 1 !== 0;
  const num = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(amount).replace(/,/g, '');
  return num + ' ' + currency;
}

/** Format number only (no currency symbol) - use with CurrencyDisplay for SAR icon */
export function formatAmount(amount: number): string {
  const hasDecimals = amount % 1 !== 0;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(amount).replace(/,/g, '');
}

export function formatNumber(num: number, lang = 'ar'): string {
  return new Intl.NumberFormat(lang === 'ar' ? 'ar-SA' : 'en-US').format(num);
}

// Build a CDN URL for store media. The subfolder MUST match where the backend
// saved the file: product images live under `/products/`, category icons/banners
// under `/categories/` (see code/routes/api/v1/dashboard/categories.js). Passing
// the wrong subfolder yields a 404 (e.g. category icons rendered as broken).
export function getCdnUrl(
  path: string,
  storeMongoId?: string,
  subfolder: 'products' | 'categories' = 'products',
  width: number | null = 250,
): string {
  const cdnBase = import.meta.env.VITE_CDN_URL || 'https://cdn.00team.com';
  // All 28 callers render a 24-64px thumbnail, but none passed a width, so the
  // CDN fell back to its 2560px default and shipped a full-size original for a
  // 40px avatar. 250 is the tier getStoreLogoUrl already uses, so these now reuse
  // a cache object that is already warm instead of minting a new one.
  //
  // Pass an explicit width for anything shown large, or `null` for the original
  // (a receipt opened in its own tab, a download).
  const q = width ? `?width=${width}` : '';
  if (storeMongoId) {
    return `${cdnBase}/stores/data/${storeMongoId}/${subfolder}/${path}${q}`;
  }
  return `${cdnBase}/${path}${q}`;
}

export function getStoreLogoUrl(logoFilename: string, width = 250): string {
  const cdnBase = import.meta.env.VITE_CDN_URL || 'https://cdn.00team.com';
  return `${cdnBase}/stores/logos/${logoFilename}?width=${width}`;
}

/** Customer-facing store URL: custom domain if set, else `<slug>.00stores.com`.
 *  Used for paylink/coupon/blog share links so the merchant copies the legacy
 *  storefront URL instead of the dashboard's `window.location.origin`. */
export function getStoreUrl(
  currentStore?: { domain?: string | null; id?: string | null } | null,
  fallbackStoreId?: string,
): string {
  if (currentStore?.domain) return `https://${currentStore.domain}`;
  const slug = currentStore?.id ?? fallbackStoreId ?? '';
  return `https://${slug}.00stores.com`;
}

export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '...';
}

/**
 * Resolve a human-readable label from a raw Torod location row (country / region /
 * city / district). Torod does NOT return `name`/`name_en`; each level has its own
 * Arabic/English keys: countries → `country_name`/`country_name_ar`, districts →
 * `name_ar`, etc. (see 00cdn `iniaiteShippingFields.js` and the storefront's
 * `TorodAddressForm`). We probe the known keys in the current language first, then
 * fall back across languages and levels so the picker never renders blank or crashes
 * on `undefined.toLowerCase()`.
 */
export function torodLabel(item: Record<string, unknown> | null | undefined, lang = 'ar'): string {
  if (!item) return '';
  const preferAr = (lang || '').toLowerCase().startsWith('ar');
  const arKeys = ['country_name_ar', 'region_name_ar', 'city_name_ar', 'name_ar', 'arName'];
  const enKeys = ['country_name', 'region_name', 'city_name', 'name_en', 'name', 'title'];
  const order = preferAr ? [...arKeys, ...enKeys] : [...enKeys, ...arKeys];
  for (const key of order) {
    const value = item[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return String(item.id ?? '');
}

/**
 * Resolve the numeric id from a raw Torod location row for the given cascade level.
 * Torod is inconsistent across levels: countries expose `id`, but regions only carry
 * `region_id` (no `id`), and a Torod city must be referenced by its `mapping_id` when
 * fetching districts (see 00cdn `iniaiteShippingFields.js`). Reading the wrong key
 * yields `undefined` → `region_id=NaN` requests and a picker that won't select.
 * Returns `null` (never `NaN`) so `enabled`/disabled guards can short-circuit cleanly.
 */
export function torodId(
  item: Record<string, unknown> | null | undefined,
  level: 'country' | 'region' | 'city' | 'district',
): number | null {
  if (!item) return null;
  const candidatesByLevel = {
    country: ['id', 'country_id'],
    region: ['region_id', 'id'],
    city: ['mapping_id', 'city_id', 'id', 'cities_id'],
    district: ['id', 'district_id'],
  };
  for (const key of candidatesByLevel[level]) {
    const value = item[key];
    if (value !== undefined && value !== null && value !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

/**
 * Translate a country name or ISO 3166-1 alpha-2 code to the current language.
 * Uses `Intl.DisplayNames` when the input looks like a 2-letter code,
 * otherwise returns the raw name (the API may already return localised text).
 */
export function formatCountryName(country: string, lang = 'ar'): string {
  if (!country) return '';
  // If it looks like a 2-letter ISO code, use Intl.DisplayNames
  if (/^[A-Z]{2}$/.test(country)) {
    try {
      const displayNames = new Intl.DisplayNames([lang], { type: 'region' });
      return displayNames.of(country) ?? country;
    } catch {
      return country;
    }
  }
  return country;
}
